import { safeBuyPath } from '@/features/billing/lib/purchaseIntent';

/** localStorage keys for auth — single source of truth. */
export const AUTH_TOKEN_KEY = 'token';
export const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';
export const AUTH_EXPIRED_MESSAGE_KEY = 'authExpiredMessage';

export const DEFAULT_AFTER_LOGIN_PATH = '/dashboard/projects';

const SAFE_REDIRECT_BASE = 'https://app.invalid';

/**
 * The stored post-login redirect, if it is one of the app's own pages:
 * anything under `/dashboard`, or a buy page that `safeBuyPath` accepts.
 * Anything else (another origin, `//host`, a backslash path) is refused, so
 * the redirect cannot leave the app.
 */
export function safeRedirectPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  if (/[\\\s]/.test(value)) return null;
  if (value.startsWith('/buy/')) return safeBuyPath(value);
  let url: URL;
  try {
    url = new URL(value, SAFE_REDIRECT_BASE);
  } catch {
    return null;
  }
  if (url.origin !== SAFE_REDIRECT_BASE) return null;
  if (url.pathname === '/dashboard' || url.pathname.startsWith('/dashboard/')) {
    return `${url.pathname}${url.search}`;
  }
  return null;
}

/** Ask the login page to come back to `path` (one of `safeRedirectPath`'s). */
export function setRedirectAfterLogin(path: string) {
  try {
    window.localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, path);
  } catch {
    /* storage blocked */
  }
}

/** Read and forget the stored post-login redirect; null unless it is safe. */
export function takeRedirectAfterLogin(): string | null {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
    if (stored !== null) window.localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
  } catch {
    /* storage blocked */
  }
  return safeRedirectPath(stored);
}

export function getAuthToken(): string | null {
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string) {
  try {
    window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    /* storage blocked */
  }
}

export function clearAuthToken() {
  try {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* storage blocked */
  }
}
